import { InjectionToken } from '@angular/core';
import { FieldNameService } from '@shared/naming/field-name.service';
import { ModelNameService } from '@shared/naming/model-name.service';
import { ProviderWithDefaults } from '../utilities/provider-with-defaults';

const defaultApiMessages = {
  unknown: () => $localize`There was an unexpected error.`,
  notFound: (model: ModelNameService, fields: FieldNameService, details?: MessageDetails) => {
    if (!details || !details.field) return $localize`${model.singular} was not found.`;

    const fieldName = fields.get(details.field);
    if (!details.value) {
      return $localize`${model.singular} with the given ${fieldName} was not found.`;
    } else {
      return $localize`${model.singular} with ${fieldName} ${details.value} was not found.`;
    }
  },
  // This is used even if your IDE says it isn't
  requiredField: (model: ModelNameService, fields: FieldNameService, details?: MessageDetails) =>
    details?.field
      ? $localize`${model.singular} is missing a value for ${fields.get(details.field)}`
      : $localize`${model.singular} is missing required information.`,
  alreadyExists: (model: ModelNameService, fields: FieldNameService, details?: MessageDetails) => {
    if (!details || !details.field) return $localize`${model.singular} already exists.`;

    if (details?.value) {
      return $localize`${model.singular} with ${fields.get(details.field)} "${details.value}" already exists.`;
    } else {
      return $localize`${model.singular} with that ${fields.get(details.field)} already exists.`;
    }
  },
  notAcceptable: (model: ModelNameService, fields: FieldNameService, details?: MessageDetails) =>
    details?.field
      ? $localize`${model.singular} has an invalid value for ${fields.get(details.field)}`
      : $localize`${model.singular} is invalid`,
  create: (model: ModelNameService) => $localize`${model.singular} was successfully created.`,
  update: (model: ModelNameService) => $localize`${model.singular} was successfully updated.`,
  delete: (model: ModelNameService) => $localize`${model.singular} was removed successfully.`,
} as const;

export type ApiMessageTemplate = (
  model: ModelNameService,
  fields: FieldNameService,
  details?: MessageDetails
) => string;
export type ApiMessages = Record<keyof typeof defaultApiMessages | string, ApiMessageTemplate>;
export type MessageDetails<Model extends Record<string, any> = any> = {
  field?: keyof Model;
  value?: any;
  record?: any;
};

const API_MESSAGES: ApiMessages = { ...defaultApiMessages };

export const ApiMessages = new InjectionToken<ApiMessages>('ApiErrorMessages', {
  factory: () => API_MESSAGES,
});

export const ApiErrorMessageProvider = ProviderWithDefaults(ApiMessages, API_MESSAGES);
